import { createContext } from 'react';
import path from 'path-browserify';
import memoizeOne from 'memoize-one';

import { TreeNode } from '../tree-types';
import { IPromisesAPI } from 'memfs/lib/promises';
import { inflateToTree } from './inflate';
import { indexFileNames } from './index-file-names';
import { renamePathPrefix } from '@bit/bit.utils.fs.rename-path';
import { traverseFromRoot } from './traverse-tree';

// import { observable, action, computed } from "mobx"

const validFilenameRegex = /^[a-zA-Z0-9_.\-]+$/;

export class TreeModel {
	private _fs?: IPromisesAPI;
	_onFileSelected?: (filepath?: string) => any;
	onTreeChange?: () => void = () => {};

	constructor({
		fs,
		workDir = '.',
		selectedFile,
	}: {
		fs?: IPromisesAPI;
		workDir?: string;
		selectedFile?: string;
	} = {}) {
		this._fs = fs;
		this._selectedFile = selectedFile;
		this.workDir = workDir;
		this.syncFromFs();
		
		// setInterval(() => this.syncFromFs(), 10000);
	}

	private _selectedFile?: string;
	get selectedFile() {
		return this._selectedFile;
	}
	set selectedFile(value: string | undefined) {
		if (this._selectedFile === value) return;

		this._selectedFile = value;
		this._onFileSelected && this._onFileSelected(value);
	}

	// @observable
	rawFiles: string[] = [];

	// @observable
	workDir: string;

	private _indexFileNames = memoizeOne(indexFileNames);
	// @computed
	private get filesSet() {
		return this._indexFileNames(this.rawFiles);
	}

	// @computed
	private _inflateFileTree = memoizeOne((rawFiles: string[]) => inflateToTree(rawFiles));
	private _getNode = memoizeOne((fileTree: TreeNode<undefined>, pathname: string) =>
		traverseFromRoot(fileTree, pathname)
	);

	// @computed
	get fileTree() {
		const tree = this._inflateFileTree(this.rawFiles);
		const node = this._getNode(tree, this.workDir) || tree;
		return node;
	}

	// @action
	syncFromFs = async () => {
		const { _fs } = this;
		if (!_fs) return;

		const rawFiles = await _fs.readdir(this.workDir);
		this.rawFiles = rawFiles as string[];

		// reaction: clear selected file, when no longer exists
		const { filesSet, selectedFile } = this;
		if (selectedFile !== undefined && !filesSet.has(selectedFile)) {
			this.selectedFile = undefined;
		}

		this.onTreeChange && this.onTreeChange();
	};

	canRename = (nextPathname: string, prevPathname?: string) => {
		if (nextPathname === prevPathname) return true;

		const { filesSet } = this;

		return !filesSet.has(nextPathname);
	};

	isValidFilename = (baseName: string) => {
		if (!baseName) return false;

		return validFilenameRegex.test(baseName);
	};

	// @action
	mkdir = async (filepath: string) => {
		if (!this._fs) return;

		const result = await this._fs.mkdir(filepath, { recursive: true });
		await this.syncFromFs();

		return result;
	};

	// @action
	unlink = async (filepath: string) => {
		if (!this._fs) return;
		const result = await this._fs.unlink(filepath);
		this.syncFromFs();
		return result;
	};

	// @action
	writeFile = async (filepath: string, content: string = '') => {
		if (!this._fs) return;

		const folder = path.dirname(filepath);
			await this._fs.mkdir(folder, { recursive: true }); //@HACK

		const result = await this._fs.writeFile(filepath, content);
		await this.syncFromFs();
		return result;
	};

	// @action
	rename = async (oldPath: string, newPath = '') => {
		if (!this._fs) return;

		const result = await this._fs.rename(oldPath, newPath);

		if (this.selectedFile !== undefined) {
			this.selectedFile = renamePathPrefix(this.selectedFile, oldPath, newPath);
		}

		this.syncFromFs();

		return result;
	};

	// @action
	selectFile = async (filepath: string) => {
		if (filepath === this.selectedFile) return;

		this.selectedFile = filepath;
	};
}

//TODO - move to another folder
export const TreeModelContext = createContext(new TreeModel());
